import React, { useState, useCallback, useEffect, useRef } from 'react';
import CameraModal from '../components/CameraModal';
import BarcodeScanner from '../components/BarcodeScanner';
import '../styles/SearchModal.css';

// step: 'barcode' | 'found' | 'photo' | 'add' | 'chz'

function speak(text) {
  try {
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = 'ru-RU';
    utt.rate = 1.05;
    window.speechSynthesis.speak(utt);
  } catch (e) {}
}

const MEDICINE_UNITS = ['мг', 'мкг', 'г', 'кг', 'мл', 'л', 'МЕ', 'ЕД'];
const RU_TO_EN = {
  'й':'q','ц':'w','у':'e','к':'r','е':'t','н':'y','г':'u','ш':'i','щ':'o','з':'p','х':'[','ъ':']',
  'ф':'a','ы':'s','в':'d','а':'f','п':'g','р':'h','о':'j','л':'k','д':'l','ж':';','э':"'",
  'я':'z','ч':'x','с':'c','м':'v','и':'b','т':'n','ь':'m','б':',','ю':'.',
  'Й':'Q','Ц':'W','У':'E','К':'R','Е':'T','Н':'Y','Г':'U','Ш':'I','Щ':'O','З':'P','Х':'{','Ъ':'}',
  'Ф':'A','Ы':'S','В':'D','А':'F','П':'G','Р':'H','О':'J','Л':'K','Д':'L','Ж':':','Э':'"',
  'Я':'Z','Ч':'X','С':'C','М':'V','И':'B','Т':'N','Ь':'M','Б':'<','Ю':'>',
};
function remapLayout(str) {
  return str.split('').map(ch => RU_TO_EN[ch] !== undefined ? RU_TO_EN[ch] : ch).join('');
}


const MEDICINE_FORMS = [
  { label: 'Таблетки', formType: 'solid' },
  { label: 'Капсулы', formType: 'solid' },
  { label: 'Порошок', formType: 'solid' },
  { label: 'Гранулы', formType: 'solid' },
  { label: 'Раствор', formType: 'liquid' },
  { label: 'Суспензия', formType: 'liquid' },
  { label: 'Сироп', formType: 'liquid' },
  { label: 'Капли', formType: 'liquid' },
  { label: 'Спрей', formType: 'liquid' },
  { label: 'Мазь/Гель/Крем', formType: 'liquid' },
  { label: 'Ампулы', formType: 'ampoule' },
];

function detectFormLabel(chz) {
  if (!chz) return 'Таблетки';
  const formNorm = (chz.form || '').toUpperCase();
  const packNorm = (chz.pack1_name || '').toUpperCase();
  if (formNorm.includes('ТАБЛЕТК')) return 'Таблетки';
  if (formNorm.includes('КАПСУЛ')) return 'Капсулы';
  if (formNorm.includes('ГРАНУЛ')) return 'Гранулы';
  if (formNorm.includes('ПОРОШОК')) return 'Порошок';
  if (packNorm.includes('АМПУЛ') || formNorm.includes('АМПУЛ')) return 'Ампулы';
  if (formNorm.includes('СУСПЕНЗИ')) return 'Суспензия';
  if (formNorm.includes('СИРОП')) return 'Сироп';
  if (formNorm.includes('КАПЛ')) return 'Капли';
  if (formNorm.includes('СПРЕЙ')) return 'Спрей';
  if (formNorm.includes('РАСТВОР')) return 'Раствор';
  if (formNorm.includes('МАЗЬ') || formNorm.includes('ГЕЛЬ') || formNorm.includes('КРЕМ') || formNorm.includes('ЛИНИМЕНТ')) return 'Мазь/Гель/Крем';
  if (chz.formType === 'ampoule') return 'Ампулы';
  if (chz.formType === 'liquid') return 'Раствор';
  return 'Таблетки';
}

function getMedicineVolumeUnit(chz) {
  if (!chz) return { volume: null, unit: 'мг' };
  const formType = chz.formType || 'solid';
  if (formType === 'solid') {
    const m = String(chz.dosage || '').match(/^([\d.,]+)\s*(\S+)/);
    if (m) {
      const value = parseFloat(m[1].replace(',', '.'));
      const unit = m[2];
      if (!isNaN(value)) return { volume: value, unit: MEDICINE_UNITS.includes(unit) ? unit : 'мг' };
    }
    return { volume: null, unit: 'мг' };
  }
  const vol = chz.pack1_size ? parseFloat(String(chz.pack1_size).replace(',', '.')) : null;
  const formNorm = (chz.form || '').toUpperCase();
  const unit = formNorm.includes('ПОРОШОК') ? 'г' : 'мл';
  return { volume: (vol && !isNaN(vol)) ? vol : null, unit };
}

function numGenitive(n) {
  const words = ['нуля','одного','двух','трёх','четырёх','пяти','шести','семи','восьми','девяти',
    'десяти','одиннадцати','двенадцати','тринадцати','четырнадцати','пятнадцати',
    'шестнадцати','семнадцати','восемнадцати','девятнадцати','двадцати'];
  return n < words.length ? words[n] : String(n);
}

const playBeep = (found) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (found) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 1400; osc.type = 'square';
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.15);
    } else {
      [0, 0.18].forEach(offset => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 400; osc.type = 'square';
        gain.gain.setValueAtTime(0.2, ctx.currentTime + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.12);
        osc.start(ctx.currentTime + offset); osc.stop(ctx.currentTime + offset + 0.12);
      });
    }
  } catch (e) {}
};

const playTripleBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.15, 0.30].forEach(offset => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 800; osc.type = 'square';
      gain.gain.setValueAtTime(0.25, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.1);
      osc.start(ctx.currentTime + offset); osc.stop(ctx.currentTime + offset + 0.1);
    });
  } catch (e) {}
};

function formatExpiry(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return mm + '/' + yyyy;
  } catch (e) {
    return dateStr;
  }
}

function SearchModal({ isOpen, onClose, activeTab, sections, onDone, manualMode = false, revisionMode = false, onRevisionMark, onAutoTab, initialItem = null, revisionIds = null }) {
  const [step, setStep] = useState('barcode');
  const [scannerActive, setScannerActive] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scannedBarcode, setScannedBarcode] = useState('');
  const barcodeInputRef = useRef(null);
  const hiddenScanInputRef = useRef(null);
  const hiddenScanBuffer = useRef('');

  const [cameraOpen, setCameraOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [foundItem, setFoundItem] = useState(null);
  const [foundSection, setFoundSection] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const [newName, setNewName] = useState('');
  const [newVolume, setNewVolume] = useState('');
  const [newSectionId, setNewSectionId] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [photoFilename, setPhotoFilename] = useState(null);

  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [isPreloaded, setIsPreloaded] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editForm, setEditForm] = useState('');
  const [editExpiry, setEditExpiry] = useState('');
  const [editProducer, setEditProducer] = useState('');
  const [editBatch, setEditBatch] = useState('');
  const [editVolumeValue, setEditVolumeValue] = useState('');
  const [editUnit, setEditUnit] = useState('');
  const [ttsEnabled, setTtsEnabled] = useState(() => {
    try { return localStorage.getItem('inventory_tts') !== 'off'; } catch { return true; }
  });
  const ttsEnabledRef = useRef(ttsEnabled);
  useEffect(() => { ttsEnabledRef.current = ttsEnabled; }, [ttsEnabled]);

  // CHZ state
  const [chzData, setChzData] = useState(null);
  const [chzEditedName, setChzEditedName] = useState('');
  const [chzPhotos, setChzPhotos] = useState([]);
  const [chzCameraOpen, setChzCameraOpen] = useState(false);
  const [volumeUnit, setVolumeUnit] = useState('мг');
  const [selectedForm, setSelectedForm] = useState('Таблетки');
  const [pack1Count, setPack1Count] = useState(1);
  const [pack2Count, setPack2Count] = useState(1);
  const [showPackHelp, setShowPackHelp] = useState(false);
  const [editCisProducer, setEditCisProducer] = useState('');
  const [editCisBatch, setEditCisBatch] = useState('');
  const [editCisExpiry, setEditCisExpiry] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    // Common resets
    setBarcodeInput('');
    setScannedBarcode('');
    setPhotos([]);
    setPhotoFilename(null);
    setNewName('');
    setNewVolume('');
    setNewSectionId(sections[0]?.sync_id || '');
    setNewUnit('');
    setSavedMsg('');
    setChzData(null);
    setChzPhotos([]);
    setVolumeUnit('мг');
    setSelectedForm('Таблетки');
    if (initialItem) {
      // Edit mode: preload specific item
      const section = sections.find(s => s.items?.some(i => i.sync_id === initialItem.sync_id));
      setFoundItem(initialItem);
      setFoundSection(section || null);
      setQuantity(initialItem.quantity ?? 0);
      setEditedName(initialItem.name || '');
      const itemForm = initialItem.form || '';
      setEditForm(itemForm);
      setSelectedForm(itemForm || 'Таблетки');
      setEditExpiry(initialItem.expiry_date ? initialItem.expiry_date.slice(0, 10) : '');
      setEditProducer(initialItem.producer || '');
      setEditBatch(initialItem.batch || '');
      setEditVolumeValue(initialItem.volume_value != null ? String(initialItem.volume_value) : '');
      setEditUnit(initialItem.unit || '');
      setIsPreloaded(true);
      setStep('found');
      setScannerActive(false);
    } else {
      setFoundItem(null);
      setFoundSection(null);
      setQuantity(1);
      setEditedName('');
      setEditForm('');
      setEditExpiry('');
      setEditProducer('');
      setEditBatch('');
      setEditVolumeValue('');
      setEditUnit('');
      setIsPreloaded(false);
      setPack1Count(1);
      setPack2Count(1);
      setShowPackHelp(false);
      setStep('barcode');
      setScannerActive(true);
      setTimeout(() => barcodeInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Авто-фокус на скрытом поле когда карточка открыта в revision mode
  useEffect(() => {
    if (revisionMode && (step === 'found' || step === 'chz')) {
      hiddenScanBuffer.current = '';
      setTimeout(() => hiddenScanInputRef.current?.focus(), 100);
    }
  }, [revisionMode, step]);

  const lookupBarcode = async (code) => {
    if (!code) return;
    if (step === 'chz' && chzData && !chzData.found_by_cis) { playTripleBeep(); return; }
    // В режиме ревизии: если скан пришёл пока карточка открыта — помечаем текущий товар зелёным
    if (revisionMode && onRevisionMark) {
      const currentId = step === 'found'
        ? foundItem?.sync_id
        : (step === 'chz' ? chzData?.existing_item?.sync_id : null);
      if (currentId) {
        onRevisionMark(currentId);
        if (onDone) onDone();
      }
    }
    setScannerActive(false);
    setLoading(true);
    setSavedMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/inventory/barcode/' + encodeURIComponent(code), {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setScannedBarcode(code);
      if (data.found) {
        playBeep(true);
        const item = sections.flatMap(s => s.items || []).find(i => i.sync_id === data.sync_id) || data;
        const section = sections.find(s => s.items?.some(i => i.sync_id === data.sync_id));
        setFoundItem(item);
        setFoundSection(section || (data.section_sync_id ? { sync_id: data.section_sync_id, category: data.category, name: data.section_name } : null));
        setQuantity(item.quantity ?? data.quantity ?? 0);
        setEditedName(item.name || '');
        setEditForm(item.form || '');
        setSelectedForm(item.form || 'Таблетки');
        setEditExpiry(item.expiry_date ? String(item.expiry_date).slice(0, 10) : '');
        setEditProducer(item.producer || '');
        setEditBatch(item.batch || '');
        setEditVolumeValue(item.volume_value != null ? String(item.volume_value) : '');
        setEditUnit(item.unit || '');
        setPack1Count(item.pack1_count || 1);
        setPack2Count(item.pack2_count || 1);
        setNewSectionId(section?.sync_id || sections[0]?.sync_id || '');
        setStep('found');
        if (ttsEnabledRef.current) {
          setTimeout(() => speak(String(item.quantity ?? 0)), 250);
        }
        // В режиме ревизии — снова включаем камеру, чтобы следующий скан авто-перешёл
        if (revisionMode) setTimeout(() => setScannerActive(true), 700);
      } else {
        playBeep(false);
        setStep('photo');
      }
    } catch (err) {
      console.error('[SearchModal] barcode lookup error:', err);
      setSavedMsg('Ошибка поиска. Попробуйте ещё раз.');
      setScannerActive(true);
    } finally {
      setLoading(false);
    }
  };

  const lookupChestnyZnak = async (code) => {
    if (step === 'chz' && chzData && !chzData.found_by_cis) { playTripleBeep(); return; }
    console.log('[lookupChestnyZnak] called, step:', step, 'code:', code.substring(0, 40));
    // В режиме ревизии: если карточка ЧЗ открыта — помечаем текущий товар зелёным
    if (revisionMode && onRevisionMark && step === 'chz' && chzData?.existing_item?.sync_id) {
      onRevisionMark(chzData.existing_item.sync_id);
      if (onDone) onDone();
    }
    setScannerActive(false);
    setLoading(true);
    setSavedMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/inventory/chestny-znak', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSavedMsg('Ошибка сервера. Попробуйте ещё раз.');
        setScannerActive(true);
        setLoading(false);
        return;
      }
      if (data.chz_unavailable) {
        setSavedMsg('Честный знак недоступен. Повторите сканирование.');
        setScannerActive(true);
        setLoading(false);
        return;
      }
      if (data.found === false) {
        playBeep(false);
        setSavedMsg('Товар не найден в Честном знаке или не является лекарством.');
        setScannerActive(true);
        setLoading(false);
        return;
      }
      setChzData(data);
      setChzEditedName((data.chz && data.chz.productName) || (data.existing_item && data.existing_item.name) || '');
      setSelectedForm(detectFormLabel(data.chz));
      if (onAutoTab) onAutoTab('medicine');
      if (!data.found_by_cis) {
        const { volume: _v, unit: _u } = getMedicineVolumeUnit(data.chz);
        setVolumeUnit(data.chz?.unit || _u || 'мг');
        setNewVolume(data.chz?.volume_value != null ? String(data.chz.volume_value) : (_v != null ? String(_v) : ''));
        setPack1Count(data.chz?.pack1_count > 1 ? data.chz.pack1_count : 1);
        setPack2Count(data.chz?.pack2_count > 1 ? data.chz.pack2_count : 1);
        setEditCisExpiry(data.chz?.expiry_date ? String(data.chz.expiry_date).slice(0, 10) : '');
        setNewSectionId(data.section_sync_id || sections[0]?.sync_id || '');
        if (ttsEnabledRef.current) setTimeout(() => speak('не найдено'), 200);
      } else if (data.existing_item) {
        setNewVolume(data.existing_item.volume_value != null ? String(data.existing_item.volume_value) : '');
        setVolumeUnit(data.existing_item.unit || 'мг');
        setPack1Count(data.existing_item.pack1_count || 1);
        setPack2Count(data.existing_item.pack2_count || 1);
        if (data.existing_item.form) setSelectedForm(data.existing_item.form);
        setNewSectionId(data.existing_item.section_sync_id || '');
        setEditCisProducer(data.existing_item.producer || '');
        setEditCisBatch(data.existing_item.batch || '');
        setEditCisExpiry(data.existing_item.expiry_date ? (typeof data.existing_item.expiry_date === 'string' ? data.existing_item.expiry_date.slice(0, 10) : new Date(data.existing_item.expiry_date).toISOString().slice(0, 10)) : '');
      }
      if (data.found_by_cis && data.existing_item) {
        playBeep(true);
        // В режиме ревизии — помечаем зелёным и показываем карточку (не сбрасываем)
        if (revisionMode && onRevisionMark) {
          onRevisionMark(data.existing_item.sync_id);
          if (ttsEnabledRef.current) {
            const itemBarcode = data.existing_item.barcode || data.chz?.barcode;
            const allItems = sections.flatMap(s => s.items || []);
            let groupItems = itemBarcode
              ? allItems.filter(i => i.barcode === itemBarcode)
              : [data.existing_item];
            if (groupItems.length === 0) groupItems = [data.existing_item];
            const alreadyMarked = groupItems.filter(i =>
              revisionIds?.has(i.sync_id) || i.sync_id === data.existing_item.sync_id
            ).length;
            const total = groupItems.length;
            setTimeout(() => speak(alreadyMarked + ' из ' + numGenitive(total)), 250);
          }
          // Показываем карточку вместо возврата к сканеру
          setStep('chz');
          setTimeout(() => setScannerActive(true), 700);
          return;
        }
        if (ttsEnabledRef.current) {
          setTimeout(() => speak(data.existing_item.name || '1'), 250);
        }
      }
      setStep('chz');
    } catch (err) {
      console.error('[SearchModal] chz lookup error:', err);
      setSavedMsg('Ошибка запроса к Честному знаку.');
      setScannerActive(true);
    } finally {
      setLoading(false);
    }
  };

  // Ref-паттерн: callback стабильный (нет перезапуска камеры), но всегда вызывает актуальную функцию
  const handleBarcodeDetectedRef = useRef(null);
  handleBarcodeDetectedRef.current = ({ code, isQR }) => {
    console.log('[SearchModal] scanned isQR:', isQR, 'code:', code.substring(0, 60));
    if (isQR) {
      lookupChestnyZnak(code);
    } else {
      lookupBarcode(code);
    }
  };

  const handleBarcodeDetected = useCallback((evt) => {
    handleBarcodeDetectedRef.current(evt);
  }, []);

  const handleBarcodeManual = () => {
    const code = barcodeInput.trim();
    if (!code) return;
    // GS1 DataMatrix (Честный знак): содержит \x1d или начинается с 01+14цифр+21
    let processCode = code;
    if (processCode.includes('\x1d') || /^01\d{14}21/.test(processCode)) {
      if (!processCode.includes('\x1d')) {
        // Нет GS-разделителей (русская раскладка или браузер отбросил \x1d).
        if (/[а-яёА-ЯЁ]/.test(processCode)) {
          const m = processCode.match(/^[\x00-\x7F]+/);
          processCode = m ? m[0] : processCode;
        }
        // Серийный номер ЧЗ — ровно 13 символов после AI "21", дальше 91/92 не нужны API
        const m = processCode.match(/^(01\d{14}21.{13})/);
        if (m) processCode = m[1];
      }
      lookupChestnyZnak(processCode);
    } else {
      lookupBarcode(processCode);
    }
  };

  const handleCameraCapture = useCallback((items) => {
    const imageItems = items.filter(i => i.type === 'image');
    setPhotos(prev => [...prev, ...imageItems].slice(0, 3));
    setCameraOpen(false);
  }, []);

  const removePhoto = (idx) => {
    setPhotos(prev => {
      prev[idx] && URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleIdentify = async () => {
    if (photos.length === 0) return;
    setLoading(true);
    setSavedMsg('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      photos.forEach(p => formData.append('photos', p.file));
      const res = await fetch('/api/inventory/identify', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
        body: formData,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPhotoFilename(data.photo_filename || null);
      setNewName(data.name || '');
      setNewVolume(data.volume_value != null ? String(data.volume_value) : '');
      setNewUnit(data.unit || '');
      setNewSectionId(sections[0]?.sync_id || '');
      setStep('add');
    } catch (err) {
      console.error('[SearchModal] identify error:', err);
      setSavedMsg('Ошибка определения. Попробуйте ещё раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuantity = async () => {
    if (!foundItem?.sync_id) return;
    if ((parseInt(quantity) || 0) === 0) {
      if (!window.confirm('Установить количество 0? Позиция исчезнет из списка.')) return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (isPreloaded) {
        // Режим редактирования: сохраняем все поля
        const isCis = !!foundItem.cis;
        // PATCH /fields — текстовые поля
        const fields = {
          name: editedName.trim() || foundItem.name,
          form: editForm || null,
        };
        if (!isCis) {
          fields.volume_value = editVolumeValue !== '' ? parseFloat(editVolumeValue) : null;
          fields.unit = editUnit || null;
        }
        const r1 = await fetch('/api/inventory/items/' + foundItem.sync_id + '/fields', {
          method: 'PATCH',
          headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
          body: JSON.stringify(fields),
        });
        if (!r1.ok) throw new Error(await r1.text());
        // PATCH /quantity — только для ШК
        if (!isCis) {
          const r2 = await fetch('/api/inventory/items/' + foundItem.sync_id + '/quantity', {
            method: 'PATCH',
            headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity: parseInt(quantity) || 0 }),
          });
          if (!r2.ok) throw new Error(await r2.text());
        }
        setSavedMsg('Сохранено!');
        onDone();
        setTimeout(() => handleClose(), 800);
        return;
      }
      // Обычный режим: сохраняем поля и количество
      await fetch('/api/inventory/items/' + foundItem.sync_id + '/fields', {
        method: 'PATCH',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editedName.trim() || foundItem.name,
          form: editForm || null,
          producer: editProducer || null,
          batch: editBatch || null,
          expiry_date: editExpiry || null,
          volume_value: editVolumeValue !== '' ? parseFloat(editVolumeValue) : null,
          unit: editUnit || null,
          pack1_count: pack1Count,
          pack2_count: pack2Count,
        }),
      });
      const res = await fetch('/api/inventory/items/' + foundItem.sync_id + '/quantity', {
        method: 'PATCH',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: parseInt(quantity) || 0 }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSavedMsg('Сохранено!');
      if (revisionMode && onRevisionMark && foundItem?.sync_id) onRevisionMark(foundItem.sync_id);
      onDone();
      if (revisionMode) {
        setTimeout(() => goBackToScanning(), 700);
      } else {
        setTimeout(() => handleClose(), 800);
      }
    } catch (err) {
      console.error('[SearchModal] save qty error:', err);
      setSavedMsg('Ошибка сохранения.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async () => {
    if (!newName.trim() || !newSectionId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const sec = sections.find(s => s.sync_id === newSectionId);
      const uc = sec?.unit_conversions || [];
      const ratioMap = Object.fromEntries(uc.filter(c => c.unit && c.ratio).map(c => [c.unit, c.ratio]));
      const effUnit = newUnit || sec?.unit || '';
      const ratio = ratioMap[effUnit];
      const volumeInBase = (newVolume !== '' && ratio && effUnit !== sec?.unit)
        ? parseFloat(newVolume) / ratio
        : (newVolume !== '' ? parseFloat(newVolume) : null);
      const body = {
        section_sync_id: newSectionId,
        name: newName.trim(),
        volume_value: volumeInBase,
        quantity: parseInt(quantity) || 1,
        photo_filename: photoFilename || undefined,
        barcode: scannedBarcode || undefined,
        ...(selectedSection?.category === 'medicine' ? {
          form: selectedForm,
          expiry_date: editExpiry || null,
          pack1_count: pack1Count,
          pack2_count: pack2Count,
        } : {}),
      };
      const res = await fetch('/api/inventory/items', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      const addedData = await res.json();
      setSavedMsg('Позиция добавлена!');
      if (revisionMode && onRevisionMark && addedData?.sync_id) {
        onRevisionMark(addedData.sync_id);
      }
      setTimeout(() => { onDone(); handleClose(); }, 800);
    } catch (err) {
      console.error('[SearchModal] add item error:', err);
      setSavedMsg('Ошибка добавления.');
    } finally {
      setSaving(false);
    }
  };

  const handleChzCameraCapture = useCallback((items) => {
    const imageItems = items.filter(i => i.type === 'image');
    setChzPhotos(prev => [...prev, ...imageItems].slice(0, 1));
    setChzCameraOpen(false);
  }, []);

  const handleArriveMedicine = async () => {
    if (!chzData || !chzData.chz) return;
    setSaving(true);
    setSavedMsg('');
    try {
      const token = localStorage.getItem('token');
      const chz = chzData.chz;
      const formType = MEDICINE_FORMS.find(f => f.label === selectedForm)?.formType || 'solid';
      let photoFilenameForMed = chzData.photo_from_barcode || null;
      if (chzPhotos.length > 0) {
        const formData = new FormData();
        formData.append('photo', chzPhotos[0].file);
        const upRes = await fetch('/api/inventory/upload-photo', {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token },
          body: formData,
        });
        if (upRes.ok) {
          const upData = await upRes.json();
          photoFilenameForMed = upData.photo_filename;
        }
      }
      const body = {
        cis: chz.cis,
        barcode: chz.barcode || undefined,
        section_sync_id: newSectionId || chzData.section_sync_id || undefined,
        ftg: chz.ftg || undefined,
        productName: chzEditedName || chz.productName,
        producer: chz.producer || undefined,
        expiry_date: editCisExpiry || chz.expiry_date || undefined,
        dosage: chz.dosage || undefined,
        form: selectedForm,
        pack1_count: pack1Count,
        pack2_count: pack2Count,
        pack1_name: chz.pack1_name || undefined,
        pack1_size: chz.pack1_size || undefined,
        batch: chz.batch || undefined,
        annotation: chz.annotation || undefined,
        photo_filename: photoFilenameForMed || undefined,
        volume_value: newVolume !== '' ? parseFloat(newVolume) : null,
        unit: volumeUnit,
      };
      const res = await fetch('/api/inventory/arrive-medicine', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      setSavedMsg('Приход записан!');
      setTimeout(() => { onDone && onDone(); handleClose(); }, 900);
    } catch (err) {
      console.error('[SearchModal] arrive medicine error:', err);
      setSavedMsg('Ошибка сохранения: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMedicineStatus = async () => {
    if (!chzData?.existing_item?.sync_id) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        name: chzEditedName.trim() || undefined,
        form: selectedForm || undefined,
        volume_value: newVolume !== '' ? parseFloat(newVolume) : null,
        unit: volumeUnit || null,
        pack1_count: pack1Count,
        pack2_count: pack2Count,
        producer: editCisProducer || null,
        batch: editCisBatch || null,
        expiry_date: editCisExpiry || null,
        section_sync_id: newSectionId || undefined,
      };
      const res = await fetch('/api/inventory/items/' + chzData.existing_item.sync_id + '/fields', {
        method: 'PATCH',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      setSavedMsg('Сохранено!');
      if (revisionMode) {
        setTimeout(() => goBackToScanning(), 700);
      } else {
        onDone();
        setTimeout(() => handleClose(), 800);
      }
    } catch (err) {
      setSavedMsg('Ошибка: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const goBackToScanning = () => {
    setIsPreloaded(false);
    setEditedName('');
    setEditForm(''); setEditExpiry('');
    setEditProducer(''); setEditBatch(''); setEditVolumeValue(''); setEditUnit('');
    setStep('barcode');
    setScannerActive(true);
    setBarcodeInput('');
    setScannedBarcode('');
    setFoundItem(null);
    setFoundSection(null);
    setPhotos([]);
    setPhotoFilename(null);
    setQuantity(1);
    setSavedMsg('');
    setChzData(null);
    setChzPhotos([]);
    setNewVolume('');
    setSelectedForm('Таблетки');
    setPack1Count(1);
    setPack2Count(1);
    setShowPackHelp(false);
    setEditCisProducer('');
    setEditCisBatch('');
    setEditCisExpiry('');
    setTimeout(() => barcodeInputRef.current?.focus(), 100);
  };

  const handleClose = () => {
    if (revisionMode && !isPreloaded && onRevisionMark && step === 'found' && foundItem?.sync_id) {
      onRevisionMark(foundItem.sync_id);
    }
    setIsPreloaded(false);
    setEditedName('');
    setEditForm(''); setEditExpiry('');
    setEditProducer(''); setEditBatch(''); setEditVolumeValue(''); setEditUnit('');
    photos.forEach(p => URL.revokeObjectURL(p.preview));
    setPhotos([]);
    setFoundItem(null);
    setFoundSection(null);
    setPhotoFilename(null);
    setBarcodeInput('');
    setScannedBarcode('');
    setQuantity(1);
    setNewName('');
    setNewVolume('');
    setNewSectionId('');
    setNewUnit('');
    setSavedMsg('');
    setScannerActive(false);
    setChzData(null);
    chzPhotos.forEach(p => URL.revokeObjectURL(p.preview));
    setChzPhotos([]);
    setVolumeUnit('мг');
    setSelectedForm('Таблетки');
    setPack1Count(1);
    setPack2Count(1);
    setShowPackHelp(false);
    setEditCisProducer('');
    setEditCisBatch('');
    setEditCisExpiry('');
    setStep('barcode');
    onClose();
  };

  if (!isOpen) return null;

  const selectedSection = sections.find(s => s.sync_id === newSectionId);
  const availableUnits = (selectedSection?.unit && selectedSection.unit !== 'шт')
    ? [selectedSection.unit, ...(selectedSection.unit_conversions || []).filter(c => c.unit).map(c => c.unit)]
    : [];
  const effectiveUnit = newUnit || selectedSection?.unit || '';
  const allUnits = [...new Set(
    sections.flatMap(s => [
      s.unit,
      ...((s.unit_conversions || []).filter(c => c.unit).map(c => c.unit))
    ]).filter(Boolean)
  )];

  const titles = { barcode: 'Добавить товар', found: isPreloaded ? 'Редактирование' : 'Найдено', photo: 'Фото товара', add: 'Новая позиция', chz: 'Честный знак' };

  return (
    <div className="sm-overlay" onClick={e => e.target === e.currentTarget && handleClose()}>
      <div className="sm-modal">
        <div className="sm-header">
          <span>{titles[step] || 'Добавить товар'}</span>
          <button
            className="sm-close"
            style={{ fontSize: 18, marginRight: 4, opacity: ttsEnabled ? 1 : 0.35 }}
            title={ttsEnabled ? 'Озвучка включена' : 'Озвучка выключена'}
            onClick={() => setTtsEnabled(v => {
              const next = !v;
              try { localStorage.setItem('inventory_tts', next ? 'on' : 'off'); } catch {}
              return next;
            })}
          >🔊</button>
          <button className="sm-close" onClick={handleClose}>X</button>
        </div>

        {step === 'barcode' && (
          <>
            {scannerActive && (
              <BarcodeScanner
                onDetected={handleBarcodeDetected}
                onClose={() => setScannerActive(false)}
              />
            )}
            {loading && <div className="sm-loading">Поиск…</div>}
            {!loading && (
              <>
                <div className="sm-field-row">
                  <input
                    ref={barcodeInputRef}
                    className="sm-text-input"
                    type="text"
                    inputMode="text"
                    value={barcodeInput}
                    onChange={e => setBarcodeInput(remapLayout(e.target.value))}
                    onKeyDown={e => e.key === 'Enter' && handleBarcodeManual()}
                    placeholder="Сканируйте или введите код"
                  />
                  <button
                    className="sm-btn sm-btn-search"
                    style={{ width: 'auto', padding: '8px 14px', flexShrink: 0 }}
                    onClick={handleBarcodeManual}
                    disabled={!barcodeInput.trim()}
                  >
                    ОК
                  </button>
                </div>
                <button className="sm-btn-force-new" onClick={() => { setStep('photo'); setScannerActive(false); }}>
                  Нет штрихкода — сфотографировать
                </button>
              </>
            )}
          </>
        )}

        {step === 'found' && foundItem && (
          <div className="sm-result sm-result-found">
            {revisionMode && (
              <input
                ref={hiddenScanInputRef}
                style={{ position: 'fixed', top: 0, left: 0, width: 1, height: 1, opacity: 0, border: 'none', outline: 'none', padding: 0, margin: 0 }}
                inputMode="none"
                value=""
                onChange={() => {}}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const code = hiddenScanBuffer.current.trim();
                    hiddenScanBuffer.current = '';
                    if (code) handleBarcodeDetected({ code, isQR: false });
                  } else if (e.key.length === 1) {
                    hiddenScanBuffer.current += e.key;
                  }
                }}
              />
            )}
            {revisionMode && scannerActive && (
              <div style={{ margin: '-12px -12px 12px', borderRadius: '8px 8px 0 0', overflow: 'hidden', height: 120 }}>
                <BarcodeScanner
                  onDetected={handleBarcodeDetected}
                  onClose={() => setScannerActive(false)}
                />
              </div>
            )}
            <div className="sm-result-label">{isPreloaded ? 'Редактирование позиции:' : 'Найдено по штрихкоду:'}</div>
            {isPreloaded ? (() => {
              const isCis = !!foundItem.cis;
              const formType = MEDICINE_FORMS.find(f => f.label === editForm)?.formType || 'solid';
              return (
                <>
                  <div className="sm-field-row">
                    <label>Название:</label>
                    <input className="sm-text-input" value={editedName} onChange={e => setEditedName(e.target.value)} autoFocus />
                  </div>
                  <div className="sm-field-row">
                    <label>Форма выпуска:</label>
                    <select className="sm-select" value={editForm} onChange={e => {
                      setEditForm(e.target.value);
                      setSelectedForm(e.target.value);
                    }}>
                      <option value="">—</option>
                      {MEDICINE_FORMS.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}
                    </select>
                  </div>
                  {!isCis && (
                    <div className="sm-field-row">
                      <label>Объём/доза:</label>
                      <input className="sm-text-input" type="number" value={editVolumeValue} onChange={e => setEditVolumeValue(e.target.value)} placeholder="—" style={{ maxWidth: 80 }} />
                      <select className="sm-select" value={editUnit} onChange={e => setEditUnit(e.target.value)} style={{ maxWidth: 80 }}>
                        <option value="">—</option>
                        {MEDICINE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  )}
                  {!isCis && (
                    <div className="sm-qty-row">
                      <label>Кол-во (шт):</label>
                      <input type="number" min="0" className="sm-qty-input" value={quantity} onChange={e => setQuantity(e.target.value)} />
                    </div>
                  )}
                </>
              );
            })() : (
              <>
                <div className="sm-found-name">{foundItem.name}</div>
                {foundSection?.category === 'medicine' && (
                  <>
                    <div className="sm-field-row" style={{ marginBottom: 12 }}>
                      <label>Форма выпуска:</label>
                      <select className="sm-select" style={{ flex: 1 }}
                        value={editForm} onChange={e => { setEditForm(e.target.value); setSelectedForm(e.target.value); }}>
                        <option value="">—</option>
                        {MEDICINE_FORMS.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}
                      </select>
                    </div>
                    <div className="sm-field-row">
                      <label>Годен до:</label>
                      <input type="date" className="sm-text-input" value={editExpiry} onChange={e => setEditExpiry(e.target.value)} />
                    </div>
                    <div className="sm-field-row" style={{ alignItems: 'flex-start' }}>
                      <label style={{ paddingTop: 6 }}>Доза / упаковка:</label>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                          <input type="number" min="0" step="0.001" className="sm-qty-input"
                            style={{ width: 64 }}
                            value={editVolumeValue}
                            onChange={e => setEditVolumeValue(e.target.value)}
                            placeholder="0" />
                          <select className="sm-select" style={{ width: 60 }}
                            value={editUnit} onChange={e => setEditUnit(e.target.value)}>
                            <option value="">—</option>
                            {MEDICINE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                          <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                          <input type="number" min="1" step="1" className="sm-qty-input"
                            style={{ width: 46 }}
                            value={pack1Count}
                            onChange={e => setPack1Count(e.target.value)} onBlur={e => setPack1Count(Math.max(1, parseInt(e.target.value) || 1))} />
                          <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                          <input type="number" min="1" step="1" className="sm-qty-input"
                            style={{ width: 46 }}
                            value={pack2Count}
                            onChange={e => setPack2Count(e.target.value)} onBlur={e => setPack2Count(Math.max(1, parseInt(e.target.value) || 1))} />
                          <button
                            style={{ background: 'none', border: '1px solid #555', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: '#888', fontSize: 11, padding: 0, lineHeight: '18px', flexShrink: 0 }}
                            onClick={() => setShowPackHelp(v => !v)}
                          >?</button>
                        </div>
                        {parseFloat(editVolumeValue) > 0 && (pack1Count > 1 || pack2Count > 1) && (
                          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                            = {+(parseFloat(editVolumeValue) * pack1Count * pack2Count).toFixed(3)} {editUnit} всего
                          </div>
                        )}
                        {showPackHelp && (
                          <div style={{ marginTop: 8, padding: '8px 10px', background: '#1a2a1a', border: '1px solid #3a5a3a', borderRadius: 6, fontSize: 12, color: '#aaa', lineHeight: 1.6 }}>
                            <div style={{ color: '#ccc', fontWeight: 600, marginBottom: 4 }}>Как заполнять:</div>
                            <div>Перекись 100мл: <span style={{ color: '#f0a030' }}>100 мл × 1 × 1</span></div>
                            <div>10 ампул по 2мл: <span style={{ color: '#f0a030' }}>2 мл × 10 × 1</span></div>
                            <div>3 блистера по 5 ампул 2мл: <span style={{ color: '#f0a030' }}>2 мл × 5 × 3</span></div>
                            <div style={{ marginTop: 6, color: '#666' }}>поле 1 — объём/доза единицы<br/>поле 2 — штук в блистере/ряду<br/>поле 3 — блистеров/рядов в пачке</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
                <div className="sm-qty-row">
                  <label>Кол-во (шт):</label>
                  <input
                    type="number"
                    min="0"
                    className="sm-qty-input"
                    value={quantity}
                    onChange={e => setQuantity(e.target.value)}
                  />
                </div>
              </>
            )}
            <button className="sm-btn sm-btn-save" onClick={handleSaveQuantity} disabled={saving}>
              {saving ? '…' : 'Сохранить'}
            </button>
            {!isPreloaded && (revisionMode ? (
              <button className="sm-btn-force-new" onClick={() => {
                if (onRevisionMark && foundItem?.sync_id) onRevisionMark(foundItem.sync_id);
                goBackToScanning();
              }}>
                Пропустить →
              </button>
            ) : (
              <button className="sm-btn-force-new" onClick={() => {
                setNewName(foundItem.name || '');
                setNewVolume(foundItem.volume_value != null ? String(foundItem.volume_value) : '');
                setNewSectionId(foundSection?.sync_id || sections[0]?.sync_id || '');
                setStep('add');
              }}>
                Другая фасовка / новая позиция
              </button>
            ))}
          </div>
        )}

        {step === 'photo' && (
          <>
            <div className="sm-photos-row">
              {photos.map((p, i) => (
                <div key={i} className="sm-photo-thumb">
                  <img src={p.preview} alt="" />
                  <button className="sm-photo-remove" onClick={() => removePhoto(i)}>X</button>
                </div>
              ))}
              {photos.length < 3 && (
                <button className="sm-add-photo" onClick={() => setCameraOpen(true)}>
                  <span>foto</span>
                  <span className="sm-add-label">{photos.length === 0 ? 'Сфотографировать' : 'Ещё фото'}</span>
                </button>
              )}
            </div>
            {photos.length > 0 && !loading && (
              <button className="sm-btn sm-btn-search" onClick={handleIdentify}>Определить</button>
            )}
            {loading && <div className="sm-loading">Определяю…</div>}
            <button className="sm-btn-force-new" onClick={() => {
              setNewName('');
              setNewVolume('');
              setNewSectionId(sections[0]?.sync_id || '');
              setNewUnit('');
              setStep('add');
            }}>
              Добавить вручную без фото
            </button>
          </>
        )}

        {step === 'add' && (
          <div className="sm-result sm-result-notfound">
            <div className="sm-field-row">
              <label>Наименование:</label>
              <input
                className="sm-text-input"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Название товара"
                autoFocus
              />
            </div>
            <div className="sm-field-row" style={{ alignItems: 'flex-start' }}>
              <label style={{ paddingTop: '8px' }}>Секция:</label>
              <div className="sm-section-picker">
                <div className="sm-section-display">
                  {selectedSection?.name || 'Выберите секцию'}
                  <span className="sm-section-arrow">v</span>
                </div>
                <select
                  className="sm-section-select"
                  value={newSectionId}
                  onChange={e => setNewSectionId(e.target.value)}
                >
                  {sections.map(s => (
                    <option key={s.sync_id} value={s.sync_id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>
            {selectedSection?.category === 'medicine' && (
              <div className="sm-field-row">
                <label>Форма выпуска:</label>
                <select className="sm-select" style={{ flex: 1 }}
                  value={selectedForm} onChange={e => setSelectedForm(e.target.value)}>
                  {MEDICINE_FORMS.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}
                </select>
              </div>
            )}
            {selectedSection?.category === 'medicine' ? (
              <>
                <div className="sm-field-row">
                  <label>Годен до:</label>
                  <input type="date" className="sm-text-input" value={editExpiry} onChange={e => setEditExpiry(e.target.value)} />
                </div>
                <div className="sm-field-row" style={{ alignItems: 'flex-start' }}>
                  <label style={{ paddingTop: 6 }}>Доза / упаковка:</label>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input type="number" min="0" step="0.001" className="sm-qty-input"
                        style={{ width: 64 }} value={newVolume}
                        onChange={e => setNewVolume(e.target.value)} placeholder="0" />
                      <select className="sm-select" style={{ width: 60 }}
                        value={effectiveUnit} onChange={e => setNewUnit(e.target.value)}>
                        <option value="">—</option>
                        {MEDICINE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                      <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                      <input type="number" min="1" step="1" className="sm-qty-input"
                        style={{ width: 46 }} value={pack1Count}
                        onChange={e => setPack1Count(e.target.value)} onBlur={e => setPack1Count(Math.max(1, parseInt(e.target.value) || 1))} />
                      <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                      <input type="number" min="1" step="1" className="sm-qty-input"
                        style={{ width: 46 }} value={pack2Count}
                        onChange={e => setPack2Count(e.target.value)} onBlur={e => setPack2Count(Math.max(1, parseInt(e.target.value) || 1))} />
                      <button
                        style={{ background: 'none', border: '1px solid #555', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: '#888', fontSize: 11, padding: 0, lineHeight: '18px', flexShrink: 0 }}
                        onClick={() => setShowPackHelp(v => !v)}>?</button>
                    </div>
                    {parseFloat(newVolume) > 0 && (pack1Count > 1 || pack2Count > 1) && (
                      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                        = {+(parseFloat(newVolume) * pack1Count * pack2Count).toFixed(3)} {effectiveUnit} всего
                      </div>
                    )}
                    {showPackHelp && (
                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#1a2a1a', border: '1px solid #3a5a3a', borderRadius: 6, fontSize: 12, color: '#aaa', lineHeight: 1.6 }}>
                        <div style={{ color: '#ccc', fontWeight: 600, marginBottom: 4 }}>Как заполнять:</div>
                        <div>Перекись 100мл: <span style={{ color: '#f0a030' }}>100 мл × 1 × 1</span></div>
                        <div>10 ампул по 2мл: <span style={{ color: '#f0a030' }}>2 мл × 10 × 1</span></div>
                        <div>3 блистера по 5 ампул 2мл: <span style={{ color: '#f0a030' }}>2 мл × 5 × 3</span></div>
                        <div style={{ marginTop: 6, color: '#666' }}>поле 1 — объём/доза единицы<br/>поле 2 — штук в блистере/ряду<br/>поле 3 — блистеров/рядов в пачке</div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="sm-field-row">
                <label>Объём:</label>
                <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="sm-qty-input"
                    style={{ flex: 1 }}
                    value={newVolume}
                    onChange={e => setNewVolume(e.target.value)}
                    placeholder="0"
                  />
                  <select className="sm-select" style={{ width: '70px' }}
                    value={effectiveUnit}
                    onChange={e => setNewUnit(e.target.value)}>
                    <option value="">—</option>
                    {allUnits.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
            )}
            <div className="sm-field-row">
              <label>Кол-во (шт):</label>
              <input
                type="number"
                min="1"
                className="sm-qty-input"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
              />
            </div>
            <button
              className="sm-btn sm-btn-save"
              onClick={handleAddItem}
              disabled={saving || !newName.trim() || !newSectionId}
            >
              {saving ? '…' : 'Добавить'}
            </button>
          </div>
        )}

        {step === 'chz' && chzData && (
          <div className="sm-result">
            {revisionMode && (
              <input
                ref={hiddenScanInputRef}
                style={{ position: 'fixed', top: 0, left: 0, width: 1, height: 1, opacity: 0, border: 'none', outline: 'none', padding: 0, margin: 0 }}
                inputMode="none"
                value=""
                onChange={() => {}}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const code = hiddenScanBuffer.current.trim();
                    hiddenScanBuffer.current = '';
                    if (code.length >= 4) {
                      const isQR = code.includes('\x1d') || /^01\d{14}21/.test(code);
                      handleBarcodeDetected({ code, isQR });
                    }
                  } else if (e.key === 'GS') {
                    hiddenScanBuffer.current += '\x1d';
                  } else if (e.key.length === 1) {
                    hiddenScanBuffer.current += e.key;
                  }
                }}
              />
            )}
            {revisionMode && scannerActive && (
              <div style={{ margin: '-12px -12px 12px', borderRadius: '8px 8px 0 0', overflow: 'hidden', height: 120 }}>
                <BarcodeScanner
                  onDetected={handleBarcodeDetected}
                  onClose={() => setScannerActive(false)}
                />
              </div>
            )}
            {loading && <div className="sm-loading">Запрос к Честному знаку…</div>}
            {!loading && (function() {
              const chz = chzData.chz || (chzData.found_by_cis && chzData.existing_item ? chzData.existing_item : null);
              const formType = MEDICINE_FORMS.find(f => f.label === selectedForm)?.formType || 'solid';
              const isFoundByCis = chzData.found_by_cis;
              return (
                <React.Fragment>
                  {!isFoundByCis && (
                    <div style={{ margin: '-4px -4px 12px', padding: '7px 12px', background: '#7a3a00', border: '1px solid #f0a030', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 18 }}>&#x2795;</span>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#f0c060', letterSpacing: 0.5 }}>НОВЫЙ ТОВАР — будет добавлен в базу</span>
                    </div>
                  )}
                  <div style={{ marginBottom: 12 }}>
                    <input
                      value={chzEditedName}
                      onChange={e => setChzEditedName(e.target.value)}
                      readOnly={revisionMode}
                      style={{ fontWeight: 600, fontSize: 15, color: '#f0a030', background: 'transparent', border: 'none', borderBottom: revisionMode ? 'none' : '1px solid #555', width: '100%', marginBottom: 4, outline: 'none', padding: '2px 0' }}
                    />
                    {chz && chz.form && (
                      <div style={{ color: '#aaa', fontSize: 13 }}>{chz.form}</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                    {chz && chz.batch && (
                      <div style={{ fontSize: 12, color: '#888' }}>
                        <span style={{ color: '#666' }}>Серия: </span>{chz.batch}
                      </div>
                    )}
                    {chz && chz.expiry_date && (
                      <div style={{ fontSize: 12, color: '#888' }}>
                        <span style={{ color: '#666' }}>До: </span>{formatExpiry(chz.expiry_date)}
                      </div>
                    )}
                    {chz && chz.expiry_date && (() => {
                      const exp = new Date(chz.expiry_date);
                      exp.setHours(23, 59, 59, 999);
                      return exp < new Date();
                    })() && (
                      <div style={{
                        marginTop: 8, padding: '8px 12px',
                        background: '#5a1010', border: '1px solid #c03030',
                        borderRadius: 6, color: '#ff6b6b', fontWeight: 600, fontSize: 13
                      }}>
                        ⚠️ Срок годности истёк! Проверьте препарат перед добавлением.
                      </div>
                    )}
                  </div>

                  {chz && chz.producer && (
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
                      <span style={{ color: '#666' }}>Производитель: </span>{chz.producer}
                    </div>
                  )}

                  <div className="sm-field-row" style={{ alignItems: 'flex-start', marginBottom: 14 }}>
                    <label style={{ paddingTop: 8 }}>Секция:</label>
                    <div className="sm-section-picker">
                      <div className="sm-section-display">
                        {selectedSection?.name || '— выберите —'}
                        <span className="sm-section-arrow">v</span>
                      </div>
                      <select className="sm-section-select" value={newSectionId} onChange={e => setNewSectionId(e.target.value)}>
                        <option value="" disabled>— выберите секцию —</option>
                        {sections.map(s => <option key={s.sync_id} value={s.sync_id}>{s.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="sm-field-row" style={{ marginBottom: 12 }}>
                    <label>Форма выпуска:</label>
                    <select className="sm-select" style={{ flex: 1 }}
                      value={selectedForm} onChange={e => setSelectedForm(e.target.value)}>
                      {MEDICINE_FORMS.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}
                    </select>
                  </div>

                  {isFoundByCis ? (
                    <React.Fragment>
                      <div className="sm-field-row">
                        <label>Годен до:</label>
                        <input type="date" className="sm-text-input" value={editCisExpiry} onChange={e => setEditCisExpiry(e.target.value)} />
                      </div>
                      <div className="sm-field-row" style={{ alignItems: 'flex-start' }}>
                        <label style={{ paddingTop: 6 }}>Доза / упаковка:</label>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="number" min="0" step="0.001" className="sm-qty-input"
                              style={{ width: 64 }}
                              value={newVolume}
                              onChange={e => setNewVolume(e.target.value)}
                              placeholder="0" />
                            <select className="sm-select" style={{ width: 60 }}
                              value={volumeUnit} onChange={e => setVolumeUnit(e.target.value)}>
                              {MEDICINE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                            <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                            <input type="number" min="1" step="1" className="sm-qty-input"
                              style={{ width: 46 }}
                              value={pack1Count}
                              onChange={e => setPack1Count(e.target.value)} onBlur={e => setPack1Count(Math.max(1, parseInt(e.target.value) || 1))} />
                            <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                            <input type="number" min="1" step="1" className="sm-qty-input"
                              style={{ width: 46 }}
                              value={pack2Count}
                              onChange={e => setPack2Count(e.target.value)} onBlur={e => setPack2Count(Math.max(1, parseInt(e.target.value) || 1))} />
                            <button
                              style={{ background: 'none', border: '1px solid #555', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: '#888', fontSize: 11, padding: 0, lineHeight: '18px', flexShrink: 0 }}
                              onClick={() => setShowPackHelp(v => !v)}
                            >?</button>
                          </div>
                          {parseFloat(newVolume) > 0 && (pack1Count > 1 || pack2Count > 1) && (
                            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                              = {+(parseFloat(newVolume) * pack1Count * pack2Count).toFixed(3)} {volumeUnit} всего
                            </div>
                          )}
                          {showPackHelp && (
                            <div style={{ marginTop: 8, padding: '8px 10px', background: '#1a2a1a', border: '1px solid #3a5a3a', borderRadius: 6, fontSize: 12, color: '#aaa', lineHeight: 1.6 }}>
                              <div style={{ color: '#ccc', fontWeight: 600, marginBottom: 4 }}>Как заполнять:</div>
                              <div>Перекись 100мл: <span style={{ color: '#f0a030' }}>100 мл × 1 × 1</span></div>
                              <div>10 ампул по 2мл: <span style={{ color: '#f0a030' }}>2 мл × 10 × 1</span></div>
                              <div>3 блистера по 5 ампул 2мл: <span style={{ color: '#f0a030' }}>2 мл × 5 × 3</span></div>
                              <div style={{ marginTop: 6, color: '#666' }}>поле 1 — объём/доза единицы<br/>поле 2 — штук в блистере/ряду<br/>поле 3 — блистеров/рядов в пачке</div>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        className="sm-btn sm-btn-save"
                        onClick={handleSaveMedicineStatus}
                        disabled={saving}
                      >
                        {saving ? '…' : revisionMode ? 'Сохранить и далее' : 'Сохранить'}
                      </button>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <div style={{ marginBottom: 14 }}>
                        {chzData.photo_from_barcode ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <img src={'/supply-photos/' + chzData.photo_from_barcode} alt="фото"
                              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }} />
                            <span style={{ fontSize: 12, color: '#888' }}>Фото из базы</span>
                          </div>
                        ) : (
                          <div>
                            {chzPhotos.length > 0 ? (
                              <div className="sm-photo-thumb" style={{ display: 'inline-block' }}>
                                <img src={chzPhotos[0].preview} alt="" />
                                <button className="sm-photo-remove" onClick={() => {
                                  URL.revokeObjectURL(chzPhotos[0].preview);
                                  setChzPhotos([]);
                                }}>X</button>
                              </div>
                            ) : (
                              <button className="sm-btn sm-btn-photo" onClick={() => setChzCameraOpen(true)}>
                                📷 Сфотографировать
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="sm-field-row" style={{ alignItems: 'flex-start' }}>
                        <label style={{ paddingTop: 6 }}>Доза / упаковка:</label>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="number" min="0" step="0.001" className="sm-qty-input"
                              style={{ width: 64 }}
                              value={newVolume}
                              onChange={e => setNewVolume(e.target.value)}
                              placeholder="0" />
                            <select className="sm-select" style={{ width: 60 }}
                              value={volumeUnit} onChange={e => setVolumeUnit(e.target.value)}>
                              {MEDICINE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                            <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                            <input type="number" min="1" step="1" className="sm-qty-input"
                              style={{ width: 46 }}
                              value={pack1Count}
                              onChange={e => setPack1Count(e.target.value)} onBlur={e => setPack1Count(Math.max(1, parseInt(e.target.value) || 1))} />
                            <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                            <input type="number" min="1" step="1" className="sm-qty-input"
                              style={{ width: 46 }}
                              value={pack2Count}
                              onChange={e => setPack2Count(e.target.value)} onBlur={e => setPack2Count(Math.max(1, parseInt(e.target.value) || 1))} />
                            <button
                              style={{ background: 'none', border: '1px solid #555', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: '#888', fontSize: 11, padding: 0, lineHeight: '18px', flexShrink: 0 }}
                              onClick={() => setShowPackHelp(v => !v)}
                            >?</button>
                          </div>
                          {parseFloat(newVolume) > 0 && (pack1Count > 1 || pack2Count > 1) && (
                            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                              = {+(parseFloat(newVolume) * pack1Count * pack2Count).toFixed(3)} {volumeUnit} всего
                            </div>
                          )}
                          {showPackHelp && (
                            <div style={{ marginTop: 8, padding: '8px 10px', background: '#1a2a1a', border: '1px solid #3a5a3a', borderRadius: 6, fontSize: 12, color: '#aaa', lineHeight: 1.6 }}>
                              <div style={{ color: '#ccc', fontWeight: 600, marginBottom: 4 }}>Как заполнять:</div>
                              <div>Перекись 100мл: <span style={{ color: '#f0a030' }}>100 мл × 1 × 1</span></div>
                              <div>10 ампул по 2мл: <span style={{ color: '#f0a030' }}>2 мл × 10 × 1</span></div>
                              <div>3 блистера по 5 ампул 2мл: <span style={{ color: '#f0a030' }}>2 мл × 5 × 3</span></div>
                              <div style={{ marginTop: 6, color: '#666' }}>поле 1 — объём/доза единицы<br/>поле 2 — штук в блистере/ряду<br/>поле 3 — блистеров/рядов в пачке</div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="sm-field-row">
                        <label>Годен до:</label>
                        <input type="date" className="sm-text-input" value={editCisExpiry} onChange={e => setEditCisExpiry(e.target.value)} />
                      </div>
                      <button
                        className={'sm-btn ' + (chzPhotos.length > 0 || chzData.photo_from_barcode ? 'sm-btn-save' : 'sm-btn-save-secondary')}
                        onClick={handleArriveMedicine} disabled={saving}>
                        {saving ? '…' : 'Записать и продолжить'}
                      </button>
                    </React.Fragment>
                  )}

                  <button
                    className="sm-btn-force-new"
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      setChzData(null);
                      setSelectedForm('Таблетки');
                      setScannerActive(true);
                      setStep('barcode');
                    }}
                  >
                    Сканировать снова
                  </button>
                </React.Fragment>
              );
            })()}
          </div>
        )}

        {savedMsg && <div className="sm-saved-msg">{savedMsg}</div>}
      </div>

      <CameraModal
        isOpen={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
        photoOnly={true}
      />
      <CameraModal
        isOpen={chzCameraOpen}
        onClose={() => setChzCameraOpen(false)}
        onCapture={handleChzCameraCapture}
        photoOnly={true}
      />
    </div>
  );
}

export default SearchModal;